import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Users, Clock, Euro, Calendar } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../../../components/ui/dialog';
import { Switch } from '../../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { companyApi } from '../../../lib/api';

export default function SlotTypesPage() {
  const [slotTypes, setSlotTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [form, setForm] = useState({
    name: '',
    duration_hours: 4,
    required_collaborators: 1,
    description: '',
    is_active: true,
    pricing_mode: 'hourly',
    fixed_price: '',
    time_slots: []
  });

  // Default time slot template
  const defaultTimeSlot = { name: '', start_time: '08:00', end_time: '12:00' };

  useEffect(() => {
    fetchSlotTypes();
  }, []);

  const fetchSlotTypes = async () => {
    try {
      const res = await companyApi.getSlotTypes();
      setSlotTypes(res.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des types de créneaux');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      duration_hours: 4,
      required_collaborators: 1,
      description: '',
      is_active: true,
      pricing_mode: 'hourly',
      fixed_price: '',
      time_slots: []
    });
  };

  const handleSubmit = async () => {
    if (!form.name || !form.duration_hours) {
      toast.error('Veuillez remplir le nom et la durée');
      return;
    }

    if (form.pricing_mode === 'fixed' && (!form.fixed_price || parseFloat(form.fixed_price) <= 0)) {
      toast.error('Veuillez définir un prix fixe valide');
      return;
    }

    // Prepare data for API
    const submitData = {
      name: form.name,
      duration_hours: form.duration_hours,
      required_collaborators: form.required_collaborators,
      description: form.description || null,
      is_active: form.is_active,
      pricing_mode: form.pricing_mode,
      fixed_price: form.pricing_mode === 'fixed' ? parseFloat(form.fixed_price) : null,
      time_slots: form.time_slots.length > 0 ? form.time_slots.filter(ts => ts.name && ts.start_time && ts.end_time) : null
    };

    try {
      if (editingType) {
        await companyApi.updateSlotType(editingType.id, submitData);
        toast.success('Type de créneau mis à jour');
      } else {
        await companyApi.createSlotType(submitData);
        toast.success('Type de créneau créé');
      }
      setIsDialogOpen(false);
      setEditingType(null);
      resetForm();
      fetchSlotTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (slotType) => {
    setEditingType(slotType);
    setForm({
      name: slotType.name,
      duration_hours: slotType.duration_hours,
      required_collaborators: slotType.required_collaborators,
      description: slotType.description || '',
      is_active: slotType.is_active,
      pricing_mode: slotType.pricing_mode || 'hourly',
      fixed_price: slotType.fixed_price?.toString() || '',
      time_slots: slotType.time_slots || []
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (slotType) => {
    if (slotType.is_default) {
      toast.error('Impossible de supprimer un type par défaut');
      return;
    }

    if (!window.confirm(`Supprimer le type "${slotType.name}" ?`)) return;

    try {
      await companyApi.deleteSlotType(slotType.id);
      toast.success('Type de créneau supprimé');
      fetchSlotTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (slotType) => {
    try {
      await companyApi.updateSlotType(slotType.id, { is_active: !slotType.is_active });
      toast.success(slotType.is_active ? 'Type désactivé' : 'Type activé');
      fetchSlotTypes();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Time slot management
  const addTimeSlot = () => {
    setForm({ ...form, time_slots: [...form.time_slots, { ...defaultTimeSlot }] });
  };

  const updateTimeSlot = (index, field, value) => {
    const newSlots = [...form.time_slots];
    newSlots[index] = { ...newSlots[index], [field]: value };
    setForm({ ...form, time_slots: newSlots });
  };

  const removeTimeSlot = (index) => {
    setForm({ ...form, time_slots: form.time_slots.filter((_, i) => i !== index) });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Types de créneaux</h1>
          <p className="text-muted-foreground">
            Configurez les types d'interventions avec prix et horaires personnalisés
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditingType(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-slot-type-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau type
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingType ? 'Modifier le type de créneau' : 'Nouveau type de créneau'}
              </DialogTitle>
              <DialogDescription>
                Définissez un type d'intervention avec ses horaires et son prix
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Nom */}
              <div className="space-y-2">
                <Label>Nom du type *</Label>
                <Input
                  placeholder="Ex: Matin, Après-midi, Journée complète"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="slot-type-name"
                />
              </div>

              {/* Durée et Collaborateurs */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Durée (heures) *</Label>
                  <Input
                    type="number"
                    min={1}
                    max={12}
                    value={form.duration_hours}
                    onChange={(e) => setForm({ ...form, duration_hours: parseInt(e.target.value) || 1 })}
                    data-testid="slot-type-duration"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Collaborateurs requis *</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={form.required_collaborators}
                    onChange={(e) => setForm({ ...form, required_collaborators: parseInt(e.target.value) || 1 })}
                    data-testid="slot-type-collaborators"
                  />
                </div>
              </div>

              {/* Mode de tarification */}
              <div className="space-y-2">
                <Label>Mode de tarification</Label>
                <Select
                  value={form.pricing_mode}
                  onValueChange={(value) => setForm({ ...form, pricing_mode: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>Taux horaire × Durée</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="fixed">
                      <div className="flex items-center gap-2">
                        <Euro className="w-4 h-4" />
                        <span>Prix fixe</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {form.pricing_mode === 'hourly' 
                    ? 'Le prix sera calculé : durée × taux horaire × nombre de collaborateurs'
                    : 'Le prix fixe sera affiché directement'}
                </p>
              </div>

              {/* Prix fixe (conditionnel) */}
              {form.pricing_mode === 'fixed' && (
                <div className="space-y-2">
                  <Label>Prix fixe (€) *</Label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="Ex: 150.00"
                    value={form.fixed_price}
                    onChange={(e) => setForm({ ...form, fixed_price: e.target.value })}
                    data-testid="slot-type-fixed-price"
                  />
                </div>
              )}

              {/* Plages horaires */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Plages horaires (optionnel)</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addTimeSlot}
                    data-testid="add-time-slot-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Ajouter
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Définissez des plages horaires précises (ex: Matin 8h-12h, Après-midi 13h-17h)
                </p>
                
                {form.time_slots.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {form.time_slots.map((slot, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                        <Input
                          placeholder="Nom (ex: Matin)"
                          value={slot.name}
                          onChange={(e) => updateTimeSlot(index, 'name', e.target.value)}
                          className="flex-1"
                        />
                        <Input
                          type="time"
                          value={slot.start_time}
                          onChange={(e) => updateTimeSlot(index, 'start_time', e.target.value)}
                          className="w-24"
                        />
                        <span className="text-muted-foreground">à</span>
                        <Input
                          type="time"
                          value={slot.end_time}
                          onChange={(e) => updateTimeSlot(index, 'end_time', e.target.value)}
                          className="w-24"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-600"
                          onClick={() => removeTimeSlot(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Description (optionnel)</Label>
                <Input
                  placeholder="Ex: Pour les grands jardins"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>

              {/* Actif */}
              <div className="flex items-center justify-between">
                <Label>Actif</Label>
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleSubmit}
                  data-testid="slot-type-submit"
                >
                  {editingType ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 font-medium">
                Types de créneaux personnalisés
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Créez des types comme "Matin 8h-12h" ou "Journée 2 personnes" avec un prix fixe ou calculé.
                Les plages horaires permettent de guider vos clients lors de la réservation.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Slot Types Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {slotTypes.map((slotType) => (
          <Card 
            key={slotType.id} 
            className={`relative ${!slotType.is_active ? 'opacity-60' : ''}`}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {slotType.name}
                    {slotType.is_default && (
                      <Badge variant="secondary" className="text-xs">Par défaut</Badge>
                    )}
                  </CardTitle>
                  {slotType.description && (
                    <CardDescription className="mt-1">{slotType.description}</CardDescription>
                  )}
                </div>
                <Switch
                  checked={slotType.is_active}
                  onCheckedChange={() => handleToggleActive(slotType)}
                />
              </div>
            </CardHeader>
            <CardContent>
              {/* Infos principales */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{slotType.duration_hours}h</span>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span>{slotType.required_collaborators} pers.</span>
                </div>
                {slotType.pricing_mode === 'fixed' && slotType.fixed_price && (
                  <div className="flex items-center gap-1 text-green-600 font-medium">
                    <Euro className="w-4 h-4" />
                    <span>{slotType.fixed_price}€</span>
                  </div>
                )}
              </div>
              
              {/* Plages horaires */}
              {slotType.time_slots && slotType.time_slots.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs text-muted-foreground mb-1">Plages horaires :</p>
                  <div className="flex flex-wrap gap-1">
                    {slotType.time_slots.map((ts, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {ts.name}: {ts.start_time}-{ts.end_time}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {!slotType.is_default && (
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleEdit(slotType)}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(slotType)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {slotTypes.length === 0 && (
        <Card className="py-12">
          <CardContent className="text-center">
            <Clock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Aucun type de créneau configuré</p>
            <p className="text-sm text-muted-foreground mt-1">
              Les types par défaut seront créés automatiquement
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
